import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, Row } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { convertDateTimeFromServer, convertDateTimeToServer, displayDefaultDateTime } from 'app/shared/util/date-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getMerchants } from 'app/entities/merchant/merchant.reducer';
import { getEntities as getPublishers } from 'app/entities/publisher/publisher.reducer';
import { createEntity, getEntity, reset, updateEntity } from './deal.reducer';

export const DealUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const merchants = useAppSelector(state => state.merchant.entities);
  const publishers = useAppSelector(state => state.publisher.entities);
  const dealEntity = useAppSelector(state => state.deal.entity);
  const loading = useAppSelector(state => state.deal.loading);
  const updating = useAppSelector(state => state.deal.updating);
  const updateSuccess = useAppSelector(state => state.deal.updateSuccess);

  const handleClose = () => {
    navigate(`/deal${location.search}`);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getMerchants({}));
    dispatch(getPublishers({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    if (values.cashBackAmt !== undefined && typeof values.cashBackAmt !== 'number') {
      values.cashBackAmt = Number(values.cashBackAmt);
    }
    if (values.buzzCommission !== undefined && typeof values.buzzCommission !== 'number') {
      values.buzzCommission = Number(values.buzzCommission);
    }
    if (values.publisherCommission !== undefined && typeof values.publisherCommission !== 'number') {
      values.publisherCommission = Number(values.publisherCommission);
    }
    if (values.redeemPoint !== undefined && typeof values.redeemPoint !== 'number') {
      values.redeemPoint = Number(values.redeemPoint);
    }
    values.validityFrom = convertDateTimeToServer(values.validityFrom);
    values.validityTo = convertDateTimeToServer(values.validityTo);
    if (values.stockLevel !== undefined && typeof values.stockLevel !== 'number') {
      values.stockLevel = Number(values.stockLevel);
    }
    values.createdAt = convertDateTimeToServer(values.createdAt);
    values.updatedAt = convertDateTimeToServer(values.updatedAt);

    const entity = {
      ...dealEntity,
      ...values,
      merchant: merchants.find(it => it.id.toString() === values.merchant?.toString()),
      publisher: publishers.find(it => it.id.toString() === values.publisher?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {
          validityFrom: displayDefaultDateTime(),
          validityTo: displayDefaultDateTime(),
          createdAt: displayDefaultDateTime(),
          updatedAt: displayDefaultDateTime(),
        }
      : {
          ...dealEntity,
          validityFrom: convertDateTimeFromServer(dealEntity.validityFrom),
          validityTo: convertDateTimeFromServer(dealEntity.validityTo),
          createdAt: convertDateTimeFromServer(dealEntity.createdAt),
          updatedAt: convertDateTimeFromServer(dealEntity.updatedAt),
          merchant: dealEntity?.merchant?.id,
          publisher: dealEntity?.publisher?.id,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="tycheApp.deal.home.createOrEditLabel" data-cy="DealCreateUpdateHeading">
            <Translate contentKey="tycheApp.deal.home.createOrEditLabel">Create or edit a Deal</Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="deal-id"
                  label={translate('tycheApp.deal.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField label={translate('tycheApp.deal.name')} id="deal-name" name="name" data-cy="name" type="text" />
              <ValidatedField
                label={translate('tycheApp.deal.cashBackCurr')}
                id="deal-cashBackCurr"
                name="cashBackCurr"
                data-cy="cashBackCurr"
                type="text"
              />
              <ValidatedField
                label={translate('tycheApp.deal.cashBackAmt')}
                id="deal-cashBackAmt"
                name="cashBackAmt"
                data-cy="cashBackAmt"
                type="text"
              />
              <ValidatedField
                label={translate('tycheApp.deal.buzzCommission')}
                id="deal-buzzCommission"
                name="buzzCommission"
                data-cy="buzzCommission"
                type="text"
              />
              <ValidatedField
                label={translate('tycheApp.deal.publisherCommission')}
                id="deal-publisherCommission"
                name="publisherCommission"
                data-cy="publisherCommission"
                type="text"
              />
              <ValidatedField
                label={translate('tycheApp.deal.redeemPoint')}
                id="deal-redeemPoint"
                name="redeemPoint"
                data-cy="redeemPoint"
                type="text"
              />
              <ValidatedField
                label={translate('tycheApp.deal.imagePath')}
                id="deal-imagePath"
                name="imagePath"
                data-cy="imagePath"
                type="text"
              />
              <ValidatedField
                label={translate('tycheApp.deal.validityFrom')}
                id="deal-validityFrom"
                name="validityFrom"
                data-cy="validityFrom"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
              />
              <ValidatedField
                label={translate('tycheApp.deal.validityTo')}
                id="deal-validityTo"
                name="validityTo"
                data-cy="validityTo"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
              />
              <ValidatedField label={translate('tycheApp.deal.type')} id="deal-type" name="type" data-cy="type" type="text" />
              <ValidatedField
                label={translate('tycheApp.deal.category')}
                id="deal-category"
                name="category"
                data-cy="category"
                type="text"
              />
              <ValidatedField
                label={translate('tycheApp.deal.instruction')}
                id="deal-instruction"
                name="instruction"
                data-cy="instruction"
                type="text"
              />
              <ValidatedField
                label={translate('tycheApp.deal.termsAndConditions')}
                id="deal-termsAndConditions"
                name="termsAndConditions"
                data-cy="termsAndConditions"
                type="text"
              />
              <ValidatedField
                label={translate('tycheApp.deal.inventoryTracking')}
                id="deal-inventoryTracking"
                name="inventoryTracking"
                data-cy="inventoryTracking"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tycheApp.deal.stockLevel')}
                id="deal-stockLevel"
                name="stockLevel"
                data-cy="stockLevel"
                type="text"
              />
              <ValidatedField
                label={translate('tycheApp.deal.createdAt')}
                id="deal-createdAt"
                name="createdAt"
                data-cy="createdAt"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
              />
              <ValidatedField
                label={translate('tycheApp.deal.createdBy')}
                id="deal-createdBy"
                name="createdBy"
                data-cy="createdBy"
                type="text"
              />
              <ValidatedField
                label={translate('tycheApp.deal.updatedAt')}
                id="deal-updatedAt"
                name="updatedAt"
                data-cy="updatedAt"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
              />
              <ValidatedField
                label={translate('tycheApp.deal.updatedBy')}
                id="deal-updatedBy"
                name="updatedBy"
                data-cy="updatedBy"
                type="text"
              />
              <ValidatedField
                id="deal-merchant"
                name="merchant"
                data-cy="merchant"
                label={translate('tycheApp.deal.merchant')}
                type="select"
              >
                <option value="" key="0" />
                {merchants
                  ? merchants.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="deal-publisher"
                name="publisher"
                data-cy="publisher"
                label={translate('tycheApp.deal.publisher')}
                type="select"
              >
                <option value="" key="0" />
                {publishers
                  ? publishers.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/deal" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button color="primary" id="save-entity" data-cy="entityCreateSaveButton" type="submit" disabled={updating}>
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default DealUpdate;
